'use client'

import { SectionLink } from '@/app/components/shared/SectionLink'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'
import { routes } from '@/app/lib/routing'

import { ANALYTICS_MODULE_NAME, ANALYTICS_MODULE_TYPE } from './ProductosLocalizados.constants'

interface ProductosLocalizadosLinkProps {
  /** Texto ya resuelto por `ProductosLocalizados.tsx` (fallback fijo o
   * `link_title` del CMS). */
  readonly linkTitle: string
  /** Localización de la página anfitriona (URL), no de `useCityContext()` —
   * ver `ProductosLocalizados.tsx`/`withLocation`. */
  readonly location: string
  /** `true` ⇒ `link_title` llegó vacío, o `link_url` llegó vacío/con el
   * placeholder `"#"`: el destino se resuelve aquí, con la localización
   * ACTUAL. `false` ⇒ usar `verbatimLinkHref` tal cual. */
  readonly usesFallbackLink: boolean
  /** Destino "tal cual" del CMS (`link_url`, ya absolutizado) — solo se usa
   * cuando `usesFallbackLink` es `false`; cadena vacía en caso contrario
   * (`ProductosLocalizados.tsx` ya descartó absolutizar un placeholder). */
  readonly verbatimLinkHref: string
  readonly openInNewTab: boolean
  readonly placement?: 'header' | 'footer'
  /** Solo con `placement="header"` (alineación propia de la cabecera). */
  readonly className?: string
}

/**
 * Enlace "ver todos" del módulo — extraído de `ProductosLocalizadosHeader`
 * para poder pintarse en DOS posiciones distintas según el ancho de
 * viewport, paridad con `eci-firefly-uikit`: dentro de la cabecera desde
 * tablet (una instancia, oculta en móvil vía `className`) y justo debajo del
 * carrusel en móvil (otra instancia, oculta desde tablet — ver
 * `ProductosLocalizados.tsx`). Cada instancia se oculta con `display: none`
 * en su rango no aplicable, así que nunca hay dos visibles/tabulables a la
 * vez: tabulación y lectores de pantalla solo encuentran la que corresponde
 * al viewport actual.
 *
 * A diferencia de `ProductosLocalizadosHeader`, no necesita
 * `useSearchParams`/`useRouter`/`usePathname` ni límite `Suspense` propio —
 * `location` le llega ya resuelta por props (la de la URL de la página
 * anfitriona, no de `useCityContext()`), así que se pinta de inmediato en
 * ambas posiciones.
 */
export function ProductosLocalizadosLink({
  linkTitle,
  location,
  usesFallbackLink,
  verbatimLinkHref,
  openInNewTab,
  placement = 'header',
  className,
}: ProductosLocalizadosLinkProps) {
  const linkHref = usesFallbackLink ? routes.loMasVendido(location) : verbatimLinkHref
  const analytics = useOptionalPageAnalytics()

  // `click` genérico (`Grid Cards.csv` → "Click en CTA 'Ver todos...' del
  // módulo") — este enlace se pinta DOS veces por breakpoint (ver doc de
  // arriba), pero solo una de las dos instancias es visible/tabulable a la
  // vez, así que nunca se duplica el push.
  function handleClick() {
    analytics?.trackClick({
      click_type: 'link',
      click_subtype: NO_APLICA,
      link_text: slugify(linkTitle),
      link_url: linkHref,
      link_domain: NO_APLICA,
      module_name: ANALYTICS_MODULE_NAME,
      module_type: ANALYTICS_MODULE_TYPE,
      nav_level: NO_APLICA,
      nav_path: NO_APLICA,
      action_detail: NO_APLICA,
      action_value: NO_APLICA,
      action_context: NO_APLICA,
    })
  }

  return (
    <SectionLink
      href={linkHref}
      // Pie móvil: sin texto propio del CMS (`usesFallbackLink`) cae al "Ver todos" genérico.
      title={placement === 'footer' && usesFallbackLink ? '' : linkTitle}
      openInNewTab={openInNewTab}
      placement={placement}
      prefetch={false}
      className={className}
      onClick={handleClick}
    />
  )
}
